import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosError,
} from "axios";
import { supabase } from "@/config/supabaseClient";
import { useRateLimitStore } from "@/stores/rateLimitStore";

// Add custom event for rate limit
// const RATE_LIMIT_EVENT = "waytora:rate-limit-reached";

const TIMEOUT_MS = 10000;
const DEFAULT_HEADERS = {
  Accept: "application/json",
  "Content-Type": "application/json",
};

const STREAMING_HEADERS = {
  Accept: "text/event-stream",
  "Content-Type": "application/json",
};

const BASE_URLS = {
  development: "https://api.waytora.com",
  production: "https://api.waytora.com",
} as const;

// Define development endpoints with their respective ports
const DEV_ENDPOINTS = {
  auth: "http://localhost:8000",
  book: "http://localhost:8001",
  search: "http://localhost:8002",
  places: "http://localhost:8003",
  transport: "http://localhost:8004",
  ai: "http://localhost:8005",
  experiences: "http://localhost:8006",
  lodgings: "http://localhost:8007",
  social: "http://localhost:8008",
  trips: "http://localhost:8009",
  content: "http://localhost:8010",
  notify: "http://localhost:8011",
} as const;

const AI_URL =
  import.meta.env.VITE_NODE_ENV === "development"
    ? DEV_ENDPOINTS.ai
    : "https://api.waytora.com/ai";

const ENDPOINTS = {
  trips: "/trips",
  auth: "/auth",
  places: "/places",
  experiences: "/experiences",
  lodgings: "/lodgings",
  search: "/search",
  transport: "/transport",
  social: "/social",
  content: "/content",
  ai: "/ai",
  book: "/book",
  notify: "/notify",
} as const;

type EndpointKey = keyof typeof ENDPOINTS;

const currentEnv = (
  import.meta.env.VITE_NODE_ENV === "development" ? "development" : "production"
) as keyof typeof BASE_URLS;

const getUrl = (endpoint: EndpointKey): string => {
  // Check if we're in development mode and if this endpoint has a specific dev URL
  if (
    import.meta.env.VITE_NODE_ENV === "development" &&
    endpoint in DEV_ENDPOINTS
  ) {
    // Use the specific development endpoint with its port
    return `${DEV_ENDPOINTS[endpoint as keyof typeof DEV_ENDPOINTS]}${
      ENDPOINTS[endpoint]
    }`;
  }
  // Otherwise use the default URL
  return `${BASE_URLS[currentEnv]}${ENDPOINTS[endpoint]}`;
};

const handleAxiosError = (error: AxiosError): Promise<never> => {
  if (error.response) {
    console.error("Response error:", error.response.data);
    console.error("Status:", error.response.status);

    // Check for rate limit error (429)
    if (error.response.status === 429) {
      useRateLimitStore
        .getState()
        .setRateLimit(
          (error.response.data as { message?: string })?.message ||
            "Rate limit exceeded. Please sign up for full access."
        );
    }
  } else if (error.request) {
    console.error("Request error:", error.request);
  } else {
    console.error("Error:", error.message);
  }
  return Promise.reject(error);
};

const createApiInstance = (endpoint: EndpointKey): AxiosInstance => {
  const instance = axios.create({
    baseURL: getUrl(endpoint),
    timeout: TIMEOUT_MS,
    headers: DEFAULT_HEADERS,
    validateStatus: (status) => status >= 200 && status < 300,
  });

  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.access_token) {
        config.headers.set("Authorization", `Bearer ${session.access_token}`);
      }
      return config;
    },
    handleAxiosError
  );

  instance.interceptors.response.use((response) => response, handleAxiosError);

  return instance;
};

const createStreamingInstance = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL,
    timeout: TIMEOUT_MS * 2,
    headers: STREAMING_HEADERS,
    responseType: "stream",
  });

  instance.interceptors.request.use(async (config) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (session?.access_token) {
      config.headers.set("Authorization", `Bearer ${session.access_token}`);
    }
    return config;
  }, handleAxiosError);

  instance.interceptors.response.use(async (response) => {
    if (response.data instanceof ReadableStream) {
      return response;
    }

    const reader = response.data.getReader();
    const decoder = new TextDecoder();

    const stream = new ReadableStream({
      async start(controller) {
        try {
          while (reader.read()) {
            const { done, value } = await reader.read();
            if (done) break;
            controller.enqueue(decoder.decode(value, { stream: true }));
          }
        } catch (error) {
          controller.error(error);
        } finally {
          controller.close();
          reader.releaseLock();
        }
      },
    });

    response.data = stream;
    return response;
  }, handleAxiosError);

  return instance;
};

type ApiEndpoint =
  | "trips"
  | "auth"
  | "places"
  | "experiences"
  | "lodgings"
  | "search"
  | "transport"
  | "social"
  | "content"
  | "ai"
  | "book"
  | "notify";

const api = {
  trips: createApiInstance("trips"),
  auth: createApiInstance("auth"),
  places: createApiInstance("places"),
  experiences: createApiInstance("experiences"),
  lodgings: createApiInstance("lodgings"),
  search: createApiInstance("search"),
  transport: createApiInstance("transport"),
  social: createApiInstance("social"),
  content: createApiInstance("content"),
  ai: createApiInstance("ai"),
  book: createApiInstance("book"),
  notify: createApiInstance("notify"),
} as const;

export default api;
